import React, { useEffect, useMemo, useState } from "react";
import RateMeCommentBlock from "./RateMeCommentBlock";
import RateMeReplyBlock from "./RateMeReplyBlock";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={cx(
        "h-11 px-4 rounded-2xl text-sm font-bold border transition-all whitespace-nowrap",
        active
          ? "bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20"
          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      )}
    >
      {children}
    </button>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.14em] text-gray-400 font-black">
        {label}
      </div>
      <div className="text-2xl font-black text-gray-950 mt-1">{value}</div>
    </div>
  );
}

function Avatar({ name }) {
  const initial = String(name || "U").trim().charAt(0).toUpperCase();

  return (
    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-black shadow-md shadow-blue-600/20 shrink-0">
      {initial}
    </div>
  );
}

function VoteBox({ score = 0, myVote = 0, onVote, disabled = false }) {
  const voteUp = () => {
    if (disabled) return;
    if (myVote === 1) onVote?.(0);
    else if (myVote === -1) onVote?.(0);
    else onVote?.(1);
  };

  const voteDown = () => {
    if (disabled) return;
    if (myVote === -1) onVote?.(0);
    else if (myVote === 1) onVote?.(0);
    else onVote?.(-1);
  };

  return (
    <div className="group relative shrink-0 flex flex-col items-center rounded-2xl border border-gray-200 bg-gray-50 px-2 py-2">
      <button
        type="button"
        disabled={disabled}
        onClick={voteUp}
        className={cx(
          "w-8 h-8 rounded-xl flex items-center justify-center text-base font-black transition",
          disabled
            ? "text-gray-200 cursor-not-allowed"
            : myVote === 1
            ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
            : "text-gray-400 hover:bg-white hover:text-blue-600"
        )}
      >
        ▲
      </button>

      <div className="min-w-9 text-center py-1 text-base font-black text-gray-950">
        {score || 0}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={voteDown}
        className={cx(
          "w-8 h-8 rounded-xl flex items-center justify-center text-base font-black transition",
          disabled
            ? "text-gray-300 cursor-not-allowed opacity-60"
            : myVote === -1
            ? "bg-rose-600 text-white shadow-md shadow-rose-600/20"
            : "text-gray-400 hover:bg-white hover:text-rose-600"
        )}
      >
        ▼
      </button>

      {disabled && (
        <div className="mt-2 text-[11px] text-slate-800 font-bold">
          Log in to vote
        </div>
      )}
    </div>
  );
}

function formatDateParts(rawDate) {
  if (!rawDate) return null;

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return null;

  return {
    day: date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function TimeBadge({ rawDate }) {
  const parts = formatDateParts(rawDate);

  if (!parts) return null;

  return (
    <div className="shrink-0 text-right min-w-[120px] pt-1">
      <div className="text-sm font-bold text-gray-700">{parts.day}</div>
      <div className="text-xs font-semibold text-gray-400 mt-1">
        posted {parts.time}
      </div>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-[28px] border border-dashed border-gray-300 bg-white/70 px-6 py-14 text-center">
      <div className="mx-auto mb-4 w-14 h-14 rounded-3xl bg-gray-100 flex items-center justify-center text-2xl">
        💬
      </div>
      <div className="text-base font-semibold text-gray-600">{children}</div>
    </div>
  );
}

function ReplyComposer({ value, onChange, onCancel, onSubmit, saving }) {
  return (
    <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50/50 p-4">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full min-h-[96px] rounded-2xl border border-blue-200 bg-white px-4 py-3 text-[16px] outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 resize-y"
        placeholder="Write a reply…"
      />

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2.5 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 text-sm font-bold text-gray-700"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || !value.trim()}
          className={cx(
            "px-5 py-2.5 rounded-xl text-sm font-black text-white",
            saving || !value.trim()
              ? "bg-blue-300 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/20"
          )}
        >
          {saving ? "Posting…" : "Post reply"}
        </button>
      </div>
    </div>
  );
}

function ThreadControls({
  sort,
  setSort,
  authorRole,
  setAuthorRole,
  page,
  pagination,
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 outline-none focus:ring-4 focus:ring-blue-100"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="ranking">Top ranked</option>
          <option value="active">Recently active</option>
        </select>

        <select
          value={authorRole}
          onChange={(e) => setAuthorRole(e.target.value)}
          className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 outline-none focus:ring-4 focus:ring-blue-100"
        >
          <option value="all">All posters</option>
          <option value="admin">Admins only</option>
          <option value="user">Users only</option>
        </select>
      </div>

      <div className="text-sm font-semibold text-gray-400">
        Page {page} of {pagination?.totalPages || 1}
      </div>
    </div>
  );
}

function RateMeFilterBar({
  onlyUnratedByMe,
  setOnlyUnratedByMe,
  canInteract,
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-violet-100 bg-white px-4 py-3 shadow-sm">
      <label
        className={cx(
          "flex items-center gap-3 text-sm font-black",
          canInteract ? "text-slate-700" : "text-slate-400"
        )}
      >
        <input
          type="checkbox"
          checked={onlyUnratedByMe}
          disabled={!canInteract}
          onChange={(e) => setOnlyUnratedByMe(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300"
        />

        <span>Only unrated by me</span>
      </label>

      {!canInteract && (
        <span className="text-xs font-bold text-slate-400">
          Log in to use this filter
        </span>
      )}

      {canInteract && onlyUnratedByMe && (
        <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-black text-violet-700">
          Showing requests you have not rated
        </span>
      )}
    </div>
  );
}

function PaginationControls({ page, setPage, pagination }) {
  const totalPages = pagination?.totalPages || 1;

  if (totalPages <= 1) return null;

  return (
    <div className="mt-6 flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className={cx(
          "px-4 py-2.5 rounded-xl text-sm font-bold border",
          page <= 1
            ? "bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed"
            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
        )}
      >
        Previous
      </button>

      <div className="text-sm font-bold text-gray-500">
        {page} / {totalPages}
      </div>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        className={cx(
          "px-4 py-2.5 rounded-xl text-sm font-bold border",
          page >= totalPages
            ? "bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed"
            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
        )}
      >
        Next
      </button>
    </div>
  );
}

function CommentCard({
  comment,
  index,
  depth = 0,
  onReply,
  replyTargetId,
  replyText,
  setReplyText,
  savingReply,
  submitReply,
  onVote,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  canInteract,
  payloadBase,
  anchor,
  onSubmitted,
  currentUsername,
}) {
  const name = comment?.username || comment?.user || "User";
  const content = comment?.content || comment?.text || comment?.comment || "";
  const anchorText =
    comment?.anchor?.text || comment?.segment_text || comment?.segmentText || "";

  const replies = comment?.replies || [];
  const isReplying = replyTargetId === comment.id;
  const isDeleted = Boolean(comment?.isDeleted || comment?.deleted);

  const isRateMe =
    comment?.kind === "rate_me" ||
    comment?.type === "rate_me" ||
    Boolean(comment?.audio?.audio_url);

  const isRateMeReply =
    comment?.kind === "rate_me_reply" ||
    comment?.type === "rate_me_reply" ||
    Boolean(comment?.ratings?.length || comment?.sentenceRating);

  const alreadyAnsweredByMe =
    isRateMe &&
    Boolean(currentUsername) &&
    replies.some((reply) => {
      const replyUsername = reply?.username || reply?.user || reply?.author;

      const replyIsRateMeReply =
        reply?.kind === "rate_me_reply" ||
        reply?.type === "rate_me_reply" ||
        Boolean(reply?.ratings?.length || reply?.sentenceRating);

      return replyIsRateMeReply && replyUsername === currentUsername;
    });

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(content);
  const [savingEdit, setSavingEdit] = useState(false);

  return (
    <article
      className={cx(
        "relative",
        depth > 0 && "ml-5 sm:ml-12 border-l-2 border-blue-100 pl-5"
      )}
    >
      <div
        className={cx(
          "group relative rounded-[28px] bg-white border shadow-sm transition-all overflow-hidden",
          isDeleted
            ? "border-gray-200 bg-gray-50/70"
            : isRateMe
            ? "border-violet-300 shadow-violet-100/80 hover:shadow-xl hover:shadow-violet-100/80"
            : "border-gray-200 hover:shadow-xl hover:shadow-gray-200/70"
        )}
      >
        <div
          className={cx(
            "absolute inset-y-0 left-0",
            isDeleted
              ? "w-1.5 bg-gray-300"
              : isRateMe
              ? "w-2 bg-violet-600"
              : depth === 0
              ? "w-2 bg-blue-600"
              : "w-1.5 bg-indigo-400"
          )}
        />

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4 sm:gap-5">
            {!isDeleted && !isRateMeReply && (
              <VoteBox
                score={comment.score || 0}
                myVote={comment.myVote || 0}
                disabled={!canInteract}
                onVote={(value) => onVote?.(comment.id, value)}
              />
            )}

            {isDeleted ? (
              <div className="w-11 h-11 rounded-2xl bg-gray-200 text-gray-400 flex items-center justify-center font-black shrink-0">
                —
              </div>
            ) : (
              <Avatar name={name} />
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-5">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3
                      className={cx(
                        "text-xl font-black truncate",
                        isDeleted ? "text-gray-400 italic" : "text-gray-950"
                      )}
                    >
                      {isDeleted ? comment.deletedLabel || "Deleted" : name}
                    </h3>

                    {!isDeleted && (
                      <span
                        className={cx(
                          "rounded-full border px-3 py-1 text-xs font-black",
                          isRateMe
                            ? "bg-violet-50 text-violet-700 border-violet-100"
                            : "bg-blue-50 text-blue-700 border-blue-100"
                        )}
                      >
                        {isRateMe
                          ? "⚔️ Rate me"
                          : depth === 0
                          ? `Thread #${index + 1}`
                          : "Reply"}
                      </span>
                    )}

                    {comment.edited && !isDeleted && (
                      <span className="rounded-full bg-gray-50 text-gray-500 border border-gray-200 px-3 py-1 text-xs font-bold">
                        edited
                      </span>
                    )}

                    {comment.resolved && !isDeleted && (
                      <span className="rounded-full bg-green-50 text-green-700 border border-green-100 px-3 py-1 text-xs font-black">
                        Resolved
                      </span>
                    )}
                  </div>

                  {!isDeleted && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                      {comment?.l1 && (
                        <span>
                          L1 language:{" "}
                          <span className="font-bold">{comment.l1}</span>
                        </span>
                      )}

                    {(comment?.chapterTitle || comment?.file) && (
  <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-bold text-gray-500">
    {comment.chapterTitle || comment.file}
  </span>
)}
                    </div>
                  )}
                </div>

                <TimeBadge
                  rawDate={
                    comment.createdAt || comment.created_at || comment.updatedAt
                  }
                />
              </div>

              {depth === 0 &&
                anchorText &&
                !isDeleted &&
                !isRateMe &&
                !isRateMeReply && (
                  <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] font-black text-blue-500 mb-1.5">
                      Referenced text
                    </div>

                    <div className="text-[15px] italic font-medium text-gray-600 leading-6 line-clamp-2">
                      “{anchorText}”
                    </div>
                  </div>
                )}

              {isRateMe && !isDeleted ? (
                <RateMeCommentBlock
                  comment={comment}
                  content={content}
                  canInteract={canInteract}
                  payloadBase={payloadBase}
                  anchor={anchor}
                  onSubmitted={onSubmitted}
                  alreadyAnswered={alreadyAnsweredByMe}
                />
              ) : isRateMeReply && !isDeleted ? (
                <RateMeReplyBlock reply={comment} />
              ) : (
                <div
                  className={cx(
                    "mt-4 rounded-2xl border px-5 py-5",
                    isDeleted
                      ? "bg-gray-50 border-dashed border-gray-300"
                      : "bg-white border-gray-200"
                  )}
                >
                  {isDeleted ? (
                    <p className="text-[17px] italic font-semibold text-gray-400">
                      This comment was deleted.
                    </p>
                  ) : editing ? (
                    <>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full min-h-[110px] rounded-2xl border border-gray-300 bg-white px-4 py-3 text-[17px] outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 resize-y"
                      />

                      <div className="mt-3 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(false);
                            setEditText(content);
                          }}
                          className="px-4 py-2.5 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 text-sm font-bold text-gray-700"
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          disabled={savingEdit || !editText.trim()}
                          onClick={async () => {
                            setSavingEdit(true);
                            await onEdit?.(comment.id, editText);
                            setSavingEdit(false);
                            setEditing(false);
                          }}
                          className={cx(
                            "px-5 py-2.5 rounded-xl text-sm font-black text-white",
                            savingEdit || !editText.trim()
                              ? "bg-blue-300 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/20"
                          )}
                        >
                          {savingEdit ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-[18px] leading-8 font-medium text-gray-950 whitespace-pre-wrap break-words">
                      {content}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center gap-4 flex-wrap">
                {!isDeleted && !isRateMeReply && (
                  <>
                    <button
                      type="button"
                      disabled={!canInteract}
                      onClick={() => {
                        if (!canInteract) return;
                        onReply(comment.id);
                      }}
                      className={cx(
                        "text-sm font-black",
                        canInteract
                          ? "text-blue-600 hover:text-blue-700"
                          : "text-gray-300 cursor-not-allowed"
                      )}
                      title={!canInteract ? "Log in to reply" : ""}
                    >
                      Reply
                    </button>

                    {canEdit?.(comment) && !editing && !isRateMe && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditText(content);
                          setEditing(true);
                        }}
                        className="text-sm font-black text-gray-500 hover:text-gray-900"
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}

                {!isDeleted && canDelete?.(comment) && (
                  <button
                    type="button"
                    onClick={() => onDelete?.(comment.id)}
                    className="text-sm font-black text-rose-500 hover:text-rose-700"
                  >
                    Delete
                  </button>
                )}

                {depth === 0 && (
                  <div className="text-sm font-semibold text-gray-400">
                    {comment.replyCount || replies.length || 0} repl
                    {(comment.replyCount || replies.length || 0) === 1
                      ? "y"
                      : "ies"}
                  </div>
                )}
              </div>

              {isReplying && canInteract && !isDeleted && !isRateMeReply && (
                <ReplyComposer
                  value={replyText}
                  onChange={setReplyText}
                  onCancel={() => {
                    onReply(null);
                    setReplyText("");
                  }}
                  onSubmit={() => submitReply(comment.id)}
                  saving={savingReply}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {replies.length > 0 && (
        <div className="mt-5 space-y-5">
          {replies.map((reply, replyIndex) => (
            <CommentCard
              key={reply.id || reply._id || replyIndex}
              comment={reply}
              index={replyIndex}
              depth={depth + 1}
              onReply={onReply}
              replyTargetId={replyTargetId}
              replyText={replyText}
              setReplyText={setReplyText}
              savingReply={savingReply}
              submitReply={submitReply}
              onVote={onVote}
              canEdit={canEdit}
              canDelete={canDelete}
              canInteract={canInteract}
              payloadBase={payloadBase}
              anchor={anchor}
              onSubmitted={onSubmitted}
              onEdit={onEdit}
              onDelete={onDelete}
              currentUsername={currentUsername}
            />
          ))}
        </div>
      )}
    </article>
  );
}

export default function ChapterCommentsPanel({
  open,
  onClose,
  subject,
  file,
  segment,
  auth,
  onCountsChange,
  chapterTitle,
}) {
  const [tab, setTab] = useState("segment");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [segmentComments, setSegmentComments] = useState([]);
  const [chapterComments, setChapterComments] = useState([]);
  const [allChapterComments, setAllChapterComments] = useState([]);
  const [rateMeComments, setRateMeComments] = useState([]);

  const [segmentPagination, setSegmentPagination] = useState(null);
  const [chapterPagination, setChapterPagination] = useState(null);
  const [allChapterPagination, setAllChapterPagination] = useState(null);
  const [rateMePagination, setRateMePagination] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sort, setSort] = useState("ranking");
  const [authorRole, setAuthorRole] = useState("all");
  const [onlyUnratedByMe, setOnlyUnratedByMe] = useState(false);

  const [composerOpen, setComposerOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [savingReply, setSavingReply] = useState(false);

  const token = localStorage.getItem("token");
  const canInteract = Boolean(token);
  const fileName = typeof file === "string" ? file : file?.file;
  const displayChapterTitle = chapterTitle || fileName || "Current chapter";

  const currentUsername =
    auth?.username ||
    auth?.user?.username ||
    auth?.currentUser?.username ||
    localStorage.getItem("username") ||
    null;

  const currentUserRole =
    auth?.role ||
    auth?.user?.role ||
    auth?.currentUser?.role ||
    localStorage.getItem("role") ||
    null;

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

 const payloadBase = useMemo(() => {
  if (!subject || !fileName) return null;

  return {
    subject: {
      author: subject.author,
      title: subject.title,
      language: subject.language,
      type: subject.type,
      owner: subject.owner || null,
    },
    file: fileName,
    chapterTitle: displayChapterTitle,
  };
}, [subject, fileName, displayChapterTitle]);

  const anchor = useMemo(() => {
    if (!segment?.text) return null;

    return {
      start: segment.start,
      end: segment.end,
      text: segment.text,
    };
  }, [segment?.start, segment?.end, segment?.text]);

  const countNestedComments = (comments) => {
    let count = 0;

    const walk = (items) => {
      items.forEach((item) => {
        count += 1;
        if (item.replies?.length) walk(item.replies);
      });
    };

    walk(comments || []);
    return count;
  };

  const canEditComment = (comment) => {
    if (comment?.isDeleted || comment?.deleted) return false;
    return Boolean(currentUsername && comment?.username === currentUsername);
  };

  const canDeleteComment = (comment) => {
    if (comment?.isDeleted || comment?.deleted) return false;

    return Boolean(
      (currentUsername && comment?.username === currentUsername) ||
        currentUserRole === "ADMIN"
    );
  };

  const activeComments =
    tab === "segment"
      ? segmentComments
      : tab === "chapter"
      ? chapterComments
      : tab === "all"
      ? allChapterComments
      : rateMeComments;

  const activePagination =
    tab === "segment"
      ? segmentPagination
      : tab === "chapter"
      ? chapterPagination
      : tab === "all"
      ? allChapterPagination
      : rateMePagination;

  const segmentCount =
    segmentPagination?.totalThreads ?? countNestedComments(segmentComments);

  const chapterCount =
    chapterPagination?.totalThreads ?? countNestedComments(chapterComments);

  const allChapterCount =
    allChapterPagination?.totalThreads ?? countNestedComments(allChapterComments);

  const rateMeCount =
    rateMePagination?.totalThreads ?? countNestedComments(rateMeComments);

  const visibleTotal =
    tab === "all"
      ? allChapterCount
      : tab === "rateMe"
      ? rateMeCount
      : segmentCount + chapterCount;

  useEffect(() => {
    setPage(1);

    if (tab === "segment") {
      setSort("ranking");
    } else {
      setSort("active");
    }
  }, [tab]);

  useEffect(() => {
    setPage(1);
  }, [onlyUnratedByMe]);

  useEffect(() => {
    if (!canInteract && onlyUnratedByMe) {
      setOnlyUnratedByMe(false);
    }
  }, [canInteract, onlyUnratedByMe]);

  const fetchComments = async () => {
    if (!payloadBase) return;

    setLoading(true);
    setError("");

    try {
      const chapterUrl = `${process.env.REACT_APP_USER_API_URL}/comments/chapter?page=${
        tab === "chapter" ? page : 1
      }&pageSize=${pageSize}&sort=${
        tab === "chapter" ? sort : "active"
      }&authorRole=${authorRole}`;

      const allChaptersUrl = `${process.env.REACT_APP_USER_API_URL}/comments/all-chapters?page=${
        tab === "all" ? page : 1
      }&pageSize=${pageSize}&sort=${
        tab === "all" ? sort : "active"
      }&authorRole=${authorRole}`;

      const rateMeUrl = `${process.env.REACT_APP_USER_API_URL}/comments/rate-me-requests?page=${
        tab === "rateMe" ? page : 1
      }&pageSize=${pageSize}&sort=${
        tab === "rateMe" ? sort : "active"
      }&authorRole=${authorRole}&onlyUnratedByMe=${
        tab === "rateMe" && onlyUnratedByMe ? "true" : "false"
      }`;

      const chapterRes = await fetch(chapterUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(payloadBase),
      });

      if (!chapterRes.ok) throw new Error(`Chapter ${chapterRes.status}`);
      const chapterJson = await chapterRes.json();

      const allChaptersRes = await fetch(allChaptersUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          subject: payloadBase.subject,
        }),
      });

      if (!allChaptersRes.ok) {
        throw new Error(`All chapters ${allChaptersRes.status}`);
      }

      const allChaptersJson = await allChaptersRes.json();

      const rateMeRes = await fetch(rateMeUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          subject: payloadBase.subject,
        }),
      });

      if (!rateMeRes.ok) {
        throw new Error(`Rate me ${rateMeRes.status}`);
      }

      const rateMeJson = await rateMeRes.json();

      let segmentJson = {
        items: [],
        page: 1,
        pageSize,
        totalThreads: 0,
        totalPages: 1,
      };

      if (anchor?.text) {
        const segmentUrl = `${process.env.REACT_APP_USER_API_URL}/comments/segment?page=${
          tab === "segment" ? page : 1
        }&pageSize=${pageSize}&sort=${
          tab === "segment" ? sort : "ranking"
        }&authorRole=${authorRole}`;

        const segmentRes = await fetch(segmentUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({
            ...payloadBase,
            anchor,
          }),
        });

        if (!segmentRes.ok) throw new Error(`Segment ${segmentRes.status}`);
        segmentJson = await segmentRes.json();
      }

      const nextChapterComments =
        chapterJson.items || chapterJson.comments || chapterJson || [];

      const nextSegmentComments =
        segmentJson.items || segmentJson.comments || segmentJson || [];

      const nextAllChapterComments =
        allChaptersJson.items ||
        allChaptersJson.comments ||
        allChaptersJson ||
        [];

      const nextRateMeComments =
        rateMeJson.items || rateMeJson.comments || rateMeJson || [];

      setChapterComments(nextChapterComments);
      setSegmentComments(nextSegmentComments);
      setAllChapterComments(nextAllChapterComments);
      setRateMeComments(nextRateMeComments);

      setChapterPagination(chapterJson.items ? chapterJson : null);
      setSegmentPagination(segmentJson.items ? segmentJson : null);
      setAllChapterPagination(allChaptersJson.items ? allChaptersJson : null);
      setRateMePagination(rateMeJson.items ? rateMeJson : null);

      onCountsChange?.({
        segment:
          segmentJson.totalThreads ?? countNestedComments(nextSegmentComments),
        chapter:
          chapterJson.totalThreads ?? countNestedComments(nextChapterComments),
        all:
          allChaptersJson.totalThreads ??
          countNestedComments(nextAllChapterComments),
        rateMe:
          rateMeJson.totalThreads ?? countNestedComments(nextRateMeComments),
      });
    } catch (e) {
      console.error("fetchComments failed:", e);
      setError("Could not load comments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setTab("segment");
    setComposerOpen(false);
    setText("");
    setReplyTargetId(null);
    setReplyText("");
    setPage(1);
  }, [open, payloadBase, anchor]);

  useEffect(() => {
    if (!open) return;

    fetchComments();
  }, [
    open,
    payloadBase,
    anchor,
    page,
    sort,
    authorRole,
    onlyUnratedByMe,
  ]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        if (replyTargetId) {
          setReplyTargetId(null);
          setReplyText("");
        } else if (composerOpen) {
          setComposerOpen(false);
        } else {
          onClose?.();
        }
      }
    };

    window.addEventListener("keydown", onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, composerOpen, replyTargetId]);

  const submitComment = async () => {
    if (!canInteract) return;
    if (!payloadBase || !text.trim() || !anchor?.text) return;

    setSaving(true);
    setError("");

    try {
      const res = await fetch(`${process.env.REACT_APP_USER_API_URL}/comments`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...payloadBase,
          content: text.trim(),
          anchor,
          parentCommentId: null,
        }),
      });

      if (!res.ok) throw new Error(`Create ${res.status}`);

      setText("");
      setComposerOpen(false);
      setTab("segment");
      setPage(1);
      await fetchComments();
    } catch (e) {
      console.error("submitComment failed:", e);
      setError("Could not save comment.");
    } finally {
      setSaving(false);
    }
  };

  const submitReply = async (parentCommentId) => {
    if (!canInteract) return;
    if (!payloadBase || !replyText.trim() || !anchor?.text || !parentCommentId) {
      return;
    }

    setSavingReply(true);
    setError("");

    try {
      const res = await fetch(`${process.env.REACT_APP_USER_API_URL}/comments`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...payloadBase,
          content: replyText.trim(),
          anchor,
          parentCommentId,
        }),
      });

      if (!res.ok) throw new Error(`Reply ${res.status}`);

      setReplyText("");
      setReplyTargetId(null);
      await fetchComments();
    } catch (e) {
      console.error("submitReply failed:", e);
      setError("Could not save reply.");
    } finally {
      setSavingReply(false);
    }
  };

  const voteComment = async (commentId, nextVote) => {
    if (!canInteract) return;
    if (!commentId) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}/vote`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ value: nextVote }),
        }
      );

      if (!res.ok) throw new Error(`Vote ${res.status}`);

      await fetchComments();
    } catch (e) {
      console.error("voteComment failed:", e);
      setError("Could not save vote.");
    }
  };

  const updateComment = async (commentId, content) => {
    if (!canInteract) return;
    if (!commentId || !content.trim()) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ content: content.trim() }),
        }
      );

      if (!res.ok) throw new Error(`Update ${res.status}`);

      await fetchComments();
    } catch (e) {
      console.error("updateComment failed:", e);
      setError("Could not update comment.");
    }
  };

  const deleteComment = async (commentId) => {
    if (!canInteract) return;
    if (!commentId) return;

    const ok = window.confirm(
      "Delete this comment? Replies will stay visible in the thread."
    );

    if (!ok) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!res.ok) throw new Error(`Delete ${res.status}`);

      await fetchComments();
    } catch (e) {
      console.error("deleteComment failed:", e);
      setError("Could not delete comment.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-gray-950/55 backdrop-blur-md"
        onPointerDown={() => onClose?.()}
      />

      <div
        className="absolute inset-0 flex items-center justify-center p-3 sm:p-6"
        onPointerDown={() => onClose?.()}
      >
        <section
          onPointerDown={(e) => e.stopPropagation()}
          className="w-full max-w-7xl h-[94svh] sm:h-[90svh] rounded-none sm:rounded-[36px] overflow-hidden bg-gray-100 shadow-2xl flex flex-col"
        >
          <header className="shrink-0 bg-white border-b border-gray-200">
            <div className="px-5 sm:px-8 py-6 flex items-start justify-between gap-5">
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">
                  {subject?.author || "Chapter comments"}
                </div>

                <h1 className="text-3xl font-black text-gray-950 tracking-tight mt-1">
                  Comments
                </h1>

                <p className="text-sm text-gray-500 mt-1 truncate">
                  {displayChapterTitle}
                </p>
              </div>

              <button
                onClick={onClose}
                type="button"
                className="w-12 h-12 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-2xl font-light"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 sm:px-8 pb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-2 overflow-x-auto">
                <TabButton
                  active={tab === "segment"}
                  onClick={() => setTab("segment")}
                >
                  This segment · {segmentCount}
                </TabButton>

                <TabButton
                  active={tab === "chapter"}
                  onClick={() => setTab("chapter")}
                >
                  Chapter · {chapterCount}
                </TabButton>

                <TabButton active={tab === "all"} onClick={() => setTab("all")}>
                  All chapters · {allChapterCount}
                </TabButton>

                <TabButton
                  active={tab === "rateMe"}
                  onClick={() => setTab("rateMe")}
                >
                  Rate me · {rateMeCount}
                </TabButton>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!canInteract) return;
                  setComposerOpen(true);
                  setTab("segment");
                }}
                disabled={!canInteract || !anchor?.text}
                className={cx(
                  "h-12 px-6 rounded-2xl text-white text-sm font-black shadow-lg",
                  canInteract && anchor?.text
                    ? "bg-blue-600 hover:bg-blue-700 shadow-blue-600/25"
                    : "bg-gray-300 cursor-not-allowed shadow-none"
                )}
                title={!canInteract ? "Log in to comment" : ""}
              >
                {canInteract ? "Add comment" : "Log in to comment"}
              </button>
            </div>
          </header>

          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[380px_1fr]">
            <aside className="hidden lg:block bg-gray-50 border-r border-gray-200 p-6 overflow-y-auto">
              <div className="sticky top-0 space-y-4">
                <div className="rounded-[28px] bg-white border border-gray-200 p-5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-gray-400 font-black">
                    {tab === "segment"
                      ? "Current segment"
                      : tab === "chapter"
                      ? "Chapter"
                      : tab === "all"
                      ? "All chapters"
                      : "Rate me requests"}
                  </div>

                  <div className="mt-3 text-base text-gray-950 leading-7 whitespace-pre-wrap">
                    {tab === "segment"
                      ? anchor?.text || "No segment selected."
                      : tab === "chapter"
                      ? displayChapterTitle
                      : tab === "all"
                      ? subject?.title || "All chapters"
                      : onlyUnratedByMe
                      ? "Pronunciation requests you have not rated yet"
                      : "Pronunciation requests across this book"}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <StatPill label="Segment" value={segmentCount} />
                  <StatPill label="Chapter" value={chapterCount} />

                  <div className="col-span-2">
                    <StatPill label="All chapters" value={allChapterCount} />
                  </div>

                  <div className="col-span-2">
                    <StatPill
                      label={onlyUnratedByMe ? "Unrated by me" : "Rate me"}
                      value={rateMeCount}
                    />
                  </div>
                </div>

                <div className="rounded-[28px] bg-gray-950 text-white p-5">
                  <div className="text-xs text-white/50 font-bold uppercase tracking-[0.16em]">
                    Total threads
                  </div>
                  <div className="text-4xl font-black mt-2">{visibleTotal}</div>
                </div>
              </div>
            </aside>

            <main className="min-h-0 flex flex-col bg-gradient-to-br from-white via-gray-50 to-blue-50/40">
              {error && (
                <div className="mx-5 sm:mx-8 mt-5 rounded-2xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-semibold">
                  {error}
                </div>
              )}

              {composerOpen && (
                <div className="shrink-0 px-5 sm:px-8 pt-5">
                  <div className="rounded-[28px] bg-white border-2 border-blue-200 shadow-xl shadow-blue-100/70 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-blue-500 font-black">
                          New segment thread
                        </div>

                        <div className="text-xl font-black text-gray-950 mt-1">
                          Start a discussion
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setComposerOpen(false)}
                        className="w-10 h-10 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-600"
                      >
                        ×
                      </button>
                    </div>

                    {anchor?.text && (
                      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="text-[10px] uppercase tracking-[0.14em] font-black text-gray-400 mb-1">
                          Referenced text
                        </div>

                        <div className="text-sm italic text-gray-500 leading-6">
                          “{anchor.text}”
                        </div>
                      </div>
                    )}

                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      className="mt-4 w-full min-h-[130px] rounded-2xl border border-gray-300 bg-white px-4 py-3 text-[17px] outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 resize-y"
                      placeholder="Write a note, correction, pronunciation tip, or discussion comment…"
                    />

                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setComposerOpen(false)}
                        className="px-5 py-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-sm font-bold text-gray-700"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={submitComment}
                        disabled={saving || !text.trim() || !anchor?.text}
                        className={cx(
                          "px-6 py-3 rounded-2xl text-sm font-black text-white",
                          saving || !text.trim() || !anchor?.text
                            ? "bg-blue-300 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/25"
                        )}
                      >
                        {saving ? "Saving…" : "Start thread"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-8 py-6">
                <ThreadControls
                  sort={sort}
                  setSort={(value) => {
                    setSort(value);
                    setPage(1);
                  }}
                  authorRole={authorRole}
                  setAuthorRole={(value) => {
                    setAuthorRole(value);
                    setPage(1);
                  }}
                  page={page}
                  pagination={activePagination}
                />

                {tab === "rateMe" && (
                  <RateMeFilterBar
                    onlyUnratedByMe={onlyUnratedByMe}
                    setOnlyUnratedByMe={setOnlyUnratedByMe}
                    canInteract={canInteract}
                  />
                )}

                {loading ? (
                  <EmptyState>Loading comments…</EmptyState>
                ) : activeComments.length ? (
                  <>
                    <div className="space-y-7 pb-2">
                      {activeComments.map((comment, i) => (
                        <CommentCard
                          key={comment.id || comment._id || i}
                          comment={comment}
                          index={(page - 1) * pageSize + i}
                          onReply={(id) => {
                            setReplyTargetId(id);
                            setReplyText("");
                          }}
                          replyTargetId={replyTargetId}
                          replyText={replyText}
                          setReplyText={setReplyText}
                          savingReply={savingReply}
                          submitReply={submitReply}
                          onVote={voteComment}
                          canEdit={canEditComment}
                          canDelete={canDeleteComment}
                          onEdit={updateComment}
                          onDelete={deleteComment}
                          canInteract={Boolean(token)}
                          payloadBase={payloadBase}
                          anchor={anchor}
                          onSubmitted={fetchComments}
                          currentUsername={currentUsername}
                        />
                      ))}
                    </div>

                    <PaginationControls
                      page={page}
                      setPage={setPage}
                      pagination={activePagination}
                    />
                  </>
                ) : (
                  <EmptyState>
                    {tab === "segment"
                      ? "No discussion for this segment yet."
                      : tab === "chapter"
                      ? "No chapter comments yet."
                      : tab === "all"
                      ? "No comments across chapters yet."
                      : onlyUnratedByMe
                      ? "No unrated Rate me requests left."
                      : "No Rate me requests yet."}
                  </EmptyState>
                )}
              </div>
            </main>
          </div>

          <footer className="shrink-0 bg-white border-t border-gray-200 px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
            <div className="text-xs font-semibold text-gray-500">
              Page {page} of {activePagination?.totalPages || 1}
            </div>

            <button
              onClick={onClose}
              type="button"
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-sm font-bold text-gray-800"
            >
              Close
            </button>
          </footer>
        </section>
      </div>
    </div>
  );
}